import { PolarAngleAxis, RadialBar, RadialBarChart } from 'recharts';

import { Badge } from '@/components/ui/badge';

interface Props {
  label: string;
  percentage: number;
  value: string;
  isDisabled?: boolean;
}

const GAUGE_WIDTH = 130;
const GAUGE_HEIGHT = 80;

// Pixels, not percentages — recharts caps percentage radii at min(width, height) / 2, i.e. 35px.
// It also centres a lone bar on `innerRadius` and takes its thickness from the gap to
// `outerRadius`, so these render the arc at radius 55-65, matching MUI's `Gauge`.
const GAUGE_CENTER_Y = 72;
const GAUGE_ARC_RADIUS = 60;
const GAUGE_ARC_THICKNESS = 10;

export const AnalyticsBox = ({ label, percentage, value, isDisabled = false }: Props) => {
  const displayPercentage = isDisabled ? 0 : percentage;
  const fill = percentage < 70 ? 'var(--primary)' : 'var(--success)';
  const textColor = isDisabled ? 'var(--muted)' : 'var(--muted-foreground)';

  return (
    <div className="flex w-fit flex-col items-center">
      <span className="text-sm leading-5 font-medium text-muted-foreground">{label}</span>
      <div className="relative" style={{ width: GAUGE_WIDTH, height: GAUGE_HEIGHT }}>
        <RadialBarChart
          width={GAUGE_WIDTH}
          height={GAUGE_HEIGHT}
          cx="50%"
          cy={GAUGE_CENTER_Y}
          innerRadius={GAUGE_ARC_RADIUS}
          outerRadius={GAUGE_ARC_RADIUS + GAUGE_ARC_THICKNESS}
          startAngle={180}
          endAngle={0}
          barCategoryGap={0}
          data={[{ name: 'value', value: displayPercentage, fill }]}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar
            // Grey, not a blue tint: the value arc is `informational` below 70%, so a tinted track
            // would differ from the filled part by opacity alone.
            background={{ fill: 'var(--border)' }}
            dataKey="value"
            cornerRadius={0}
          />
        </RadialBarChart>
        <span
          // bottom-2.5 centres the text 12px above the arc baseline, inside the curve.
          className="absolute inset-x-0 bottom-2.5 text-center text-sm font-bold"
          style={{ color: textColor }}
        >
          {displayPercentage}%
        </span>
      </div>
      <Badge type="secondary" flavour="neutral">
        {isDisabled ? '-/-' : value}
      </Badge>
    </div>
  );
};
